import type { IUserInfo } from "./account/account-models";
import { API_ROOT, IS_PROD, SLACK_HOOK_URL, VAPID_PUBLIC_KEY } from "./globals";
import type { ServiceWorkerMessage } from "./service-worker-messages";
import { authToken } from "./utils/auth-token";
import {
  isNotificationSupported,
  isServiceWorkerSupported,
} from "./utils/detectors";

// `updateBadge` is the one message that doesn't require notification
// support (badges and notifications are separate browser capabilities),
// so `requireNotificationSupport` defaults to matching every other
// message's existing gating and is opted out of just for that one.
function postToServiceWorker(
  message: ServiceWorkerMessage,
  { requireNotificationSupport = true } = {}
) {
  if (isServiceWorkerSupported) {
    void navigator.serviceWorker.ready.then(
      (swRegistration: ServiceWorkerRegistration) => {
        if (
          swRegistration.active &&
          (!requireNotificationSupport || isNotificationSupported)
        ) {
          swRegistration.active.postMessage(JSON.stringify(message));
        }
      }
    );
  }
}

interface IBeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredInstallPrompt: IBeforeInstallPromptEvent | null = null;

export function init() {
  window.addEventListener("beforeinstallprompt", (e) => {
    // cache the event so it can be triggered later.
    deferredInstallPrompt = e as IBeforeInstallPromptEvent;
    void deferredInstallPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === "accepted") {
        // deferredInstallPrompt = null;
      }
    });
  });

  window.addEventListener("appinstalled", () => {
    // deferredInstallPrompt = null;
  });
}

export function isInstallPossible() {
  return !!deferredInstallPrompt;
}

export function promptInstall() {
  if (deferredInstallPrompt) {
    void deferredInstallPrompt.prompt();
  }
}

export function registerServiceWorker() {
  if (isServiceWorkerSupported) {
    void navigator.serviceWorker.register("/service-worker.js");
  }
}

export function requestUpdateServiceWorker() {
  if (isServiceWorkerSupported) {
    void navigator.serviceWorker.ready.then(
      (swRegistration: ServiceWorkerRegistration) => {
        if (swRegistration.active) {
          void swRegistration.update();
        }
      }
    );
  }
}

export function setupPushManager(user: IUserInfo) {
  postToServiceWorker({
    type: "setup",
    userId: user._id,
    username: user.username,
    accessToken: authToken(),
    apiRoot: API_ROOT,
    slackHookUrl: SLACK_HOOK_URL!,
    slackChannel: IS_PROD ? "#web-push-debug-prod" : "#web-push-debug-dev",
    vapidPublicKey: VAPID_PUBLIC_KEY!,
  });
}

export function ensureSubscription() {
  postToServiceWorker({ type: "ensureSubscription" });
}

export function deleteSubscription() {
  postToServiceWorker({ type: "logout" });
}

export function updateBadge(count: number) {
  postToServiceWorker(
    { type: "updateBadge", count },
    { requireNotificationSupport: false }
  );
}

export function pushNotification(subject: string, body: string) {
  postToServiceWorker({ type: "internalNotification", subject, body });
}

export const requestNotificationPermission = async () => {
  const permission = await Notification.requestPermission();
  if (permission === "granted") {
    ensureSubscription();
    pushNotification(
      "Notifications enabled 🎉",
      "Thank you for enabling notifications!"
    );
  }
};
